import { baseApi } from "./baseApi";
import { PaginationArgs, ScopeType } from "types/globalTypes";
import { buildQueryParams } from "services/helper";
import {
  Assessment,
  AssessmentResponse,
  Criteria,
  Group,
  GroupCriteriaPayload,
  OrganizationAssessment,
  OrganizationAssessmentResponse,
  OrganizationResponding,
  OrganizationRespondingResponse,
  QuantitativeValuesPayload,
  ReportData,
  Responder,
  Responding,
  RespondingResponse,
  TransactionDTO,
  TransactionsListResponse,
} from "types/assessmentTypes";

export const assessmentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // group
    getGroups: builder.query<Group[], PaginationArgs>({
      query: (args) => `/group?${buildQueryParams(args || {})}`,
      providesTags: ["Groups"],
      keepUnusedDataFor: 604800,
    }),

    getGroupById: builder.query<Group, number>({
      query: (id) => `/group/${id}`,
      providesTags: ["Groups"],
    }),

    createGroup: builder.mutation<Group, Partial<Group>>({
      query: (newData) => ({
        url: "/group",
        method: "POST",
        body: newData,
      }),
      invalidatesTags: ["Groups"],
    }),

    updateGroup: builder.mutation<Group, { id: number; data: Partial<Group> }>({
      query: ({ id, data }) => ({
        url: `/group/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Groups"],
    }),

    deleteGroup: builder.mutation<void, number>({
      query: (id) => ({
        url: `/group/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Groups"],
    }),

    // criteria
    getCriterias: builder.query<Criteria[], void>({
      query: () => `/criteria`,
      providesTags: ["Criterias"],
      keepUnusedDataFor: 604800,
    }),

    createGroupCriteria: builder.mutation<void, GroupCriteriaPayload>({
      query: (newData) => ({
        url: "/group-criteria",
        method: "POST",
        body: newData,
      }),
      invalidatesTags: ["Groups"],
    }),

    updateGroupCriteria: builder.mutation<void, GroupCriteriaPayload>({
      query: (newData) => ({
        url: "/group-criteria",
        method: "PUT",
        body: newData,
      }),
      invalidatesTags: ["Groups"],
    }),

    // responding
    getResponderByNationalCode: builder.query<{ responder: Responder }, string>(
      {
        query: (nationalCode) => `/company-responder/company/${nationalCode}`,
      }
    ),

    createCompanyResponder: builder.mutation<
      { message: string; responder: Responder },
      Partial<Responder>
    >({
      query: (newData) => ({
        url: "/company-responder/company",
        method: "POST",
        body: newData,
      }),
    }),

    getResponding: builder.query<RespondingResponse, PaginationArgs>({
      query: (args) => `/responding?${buildQueryParams(args || {})}`,
    }),

    getRespondingByCompanyId: builder.query<
      Responding[],
      { companyId: number; args: PaginationArgs }
    >({
      query: ({ companyId, args }) =>
        `/responding/company/${companyId}?${buildQueryParams(args || {})}`,
    }),

    createResponding: builder.mutation<
      Responding,
      { companyId: number; items: { groupId: number; responderId: number }[] }
    >({
      query: (newData) => ({
        url: "/responding",
        method: "POST",
        body: newData,
      }),
    }),

    updateResponding: builder.mutation<
      Responding,
      { id: number; status: "ACTIVE" | "DEACTIVE" }
    >({
      query: ({ id, status }) => ({
        url: `/responding/company/${id}`,
        method: "PUT",
        body: { status },
      }),
    }),

    deleteResponding: builder.mutation<void, number>({
      query: (id) => ({ url: `/responding/${id}`, method: "DELETE" }),
    }),

    // organization responding
    getOrganizationResponding: builder.query<
      OrganizationRespondingResponse,
      PaginationArgs
    >({
      query: (args) =>
        `/organization-responding?${buildQueryParams(args || {})}`,
      providesTags: ["OrganizationResponding"],
    }),

    createOrganizationResponding: builder.mutation<
      OrganizationResponding,
      { organizationId: number; items: number[] }
    >({
      query: (newData) => ({
        url: "/organization-responding",
        method: "POST",
        body: newData,
      }),
      invalidatesTags: ["OrganizationResponding"],
    }),

    statusOrganizationResponding: builder.mutation<void, number>({
      query: (id) => ({
        url: `/organization-responding/${id}/status`,
        method: "PUT",
      }),
      invalidatesTags: ["OrganizationResponding"],
    }),

    // organization assessment
    getOrganizationAssessment: builder.query<
      OrganizationAssessmentResponse,
      PaginationArgs & {
        scope?: ScopeType;
      }
    >({
      query: ({ scope, ...args }) => {
        const scopePaths: Record<ScopeType, string> = {
          admin: "/admin/organization-assessment",
          organization: "/user-organization-assessment",
          user: "",
          responder: "",
        };
        const path = scopePaths[scope] || scopePaths.user;
        return {
          url: `${path}?${buildQueryParams(args || {})}`,
        };
      },

      providesTags: ["Assessments"],
    }),

    getOrganizationAssessmentById: builder.query<
      OrganizationAssessment,
      { scope?: ScopeType; id: number }
    >({
      query: ({ scope, id }) => {
        const scopePaths: Record<ScopeType, string> = {
          admin: `/admin/organization-assessment/${id}`,
          organization: `/user-organization-assessment/${id}`,
          user: "",
          responder: "",
        };
        const path = scopePaths[scope] || scopePaths.user;
        return {
          url: `${path}`,
        };
      },
    }),

    createOrganizationAssessment: builder.mutation<
      { data: OrganizationAssessment; message: string },
      { companyId: number }
    >({
      query: (newData) => ({
        url: "/user-organization-assessment",
        method: "POST",
        body: newData,
      }),
      invalidatesTags: ["OrganizationAssessments"],
    }),

    completeOrganizationAssessment: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/user-organization-assessment/complete/${id}`,
        method: "GET",
      }),
      invalidatesTags: ["OrganizationAssessments"],
    }),

    // assessment
    getAssessments: builder.query<
      AssessmentResponse,
      PaginationArgs & {
        scope?: ScopeType;
      }
    >({
      query: ({ scope, ...args }) => {
        const scopePaths: Record<ScopeType, string> = {
          admin: "/admin/assessment",
          organization: "/user/organization/assessment",
          user: "/user/assessment",
          responder: "",
        };
        const path = scopePaths[scope] || scopePaths.user;
        return {
          url: `${path}?${buildQueryParams(args || {})}`,
        };
      },

      providesTags: ["Assessments"],
    }),

    getAssessmentById: builder.query<
      Assessment,
      { scope: ScopeType; id: number }
    >({
      query: ({ scope, id }) => {
        const scopePaths: Record<ScopeType, string> = {
          admin: `/admin/assessment/${id}`,
          organization: `/user/organization/assessment/${id}`,
          user: `/user/assessment/${id}`,
          responder: ``,
        };
        const path = scopePaths[scope] || scopePaths.user;
        return {
          url: path,
        };
      },
    }),

    createAssessment: builder.mutation<
      { data: Assessment; message: string },
      { companyId: number; licenseKey: string; organizationId: number }
    >({
      query: (newData) => ({
        url: "/user/assessment",
        method: "POST",
        body: newData,
      }),
      invalidatesTags: ["Assessments"],
    }),

    transferAssessment: builder.mutation<
      { data: Assessment; message: string },
      { assessmentId: number; licenseKey: string; organizationId: number }
    >({
      query: ({ assessmentId, licenseKey, organizationId }) => ({
        url: `/user/assessment/${assessmentId}/transfer/${organizationId}/${licenseKey}`,
        method: "POST",
      }),
      invalidatesTags: ["Assessments"],
    }),

    completeAssessment: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/user/assessment/${id}/complete`,
        method: "POST",
      }),
      invalidatesTags: ["Assessments"],
    }),

    adminRestartAssessment: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/admin/assessment/restart/${id}`,
        method: "PUT",
      }),
      invalidatesTags: ["Assessments"],
    }),

    userRestartAssessment: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/user/assessment/restart/${id}`,
        method: "PUT",
      }),
      invalidatesTags: ["Assessments"],
    }),

    answerQuantitative: builder.mutation<void, QuantitativeValuesPayload>({
      query: ({ assessmentId, values }) => ({
        url: `/user/assessment/${assessmentId}/quantitative-values`,
        method: "POST",
        body: {
          values,
        },
      }),
      invalidatesTags: ["Assessments"],
    }),

    updateAssessmentReport: builder.mutation<
      Assessment,
      { id: number; body: ReportData }
    >({
      query: ({ id, body }) => ({
        url: `/user/assessment/${id}/report`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Assessments"],
    }),

    // transaction
    getTransactionStatus: builder.query<TransactionDTO, number>({
      query: (id) => `/payment/transaction/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Transactions", id }],
    }),

    getTransactions: builder.query<
      TransactionsListResponse,
      PaginationArgs & {
        scope?: ScopeType;
      }
    >({
      query: ({ scope, ...args }) =>
        `/payment/${scope}/transactions?${buildQueryParams(args || {})}`,
      providesTags: ["Transactions"],
    }),
  }),
});

export const {
  // group
  useGetGroupsQuery,
  useLazyGetGroupsQuery,
  useGetGroupByIdQuery,
  useCreateGroupMutation,
  useUpdateGroupMutation,
  useDeleteGroupMutation,
  // criteria
  useGetCriteriasQuery,
  useCreateGroupCriteriaMutation,
  useUpdateGroupCriteriaMutation,
  // responding
  useGetResponderByNationalCodeQuery,
  useLazyGetResponderByNationalCodeQuery,
  useCreateCompanyResponderMutation,
  useGetRespondingQuery,
  useGetRespondingByCompanyIdQuery,
  useCreateRespondingMutation,
  useUpdateRespondingMutation,
  useDeleteRespondingMutation,
  // organization responding
  useGetOrganizationRespondingQuery,
  useCreateOrganizationRespondingMutation,
  useStatusOrganizationRespondingMutation,
  // organization assessment
  useGetOrganizationAssessmentQuery,
  useGetOrganizationAssessmentByIdQuery,
  useCreateOrganizationAssessmentMutation,
  useCompleteOrganizationAssessmentMutation,
  // assessment
  useGetAssessmentsQuery,
  useGetAssessmentByIdQuery,
  useCreateAssessmentMutation,
  useTransferAssessmentMutation,
  useCompleteAssessmentMutation,
  useAnswerQuantitativeMutation,
  useUpdateAssessmentReportMutation,
  // restart
  useAdminRestartAssessmentMutation,
  useUserRestartAssessmentMutation,
  // transaction
  useGetTransactionStatusQuery,
  useGetTransactionsQuery,
} = assessmentApi;
